import {
  AzureGroup,
  ClassCourse as ClassCourseModel,
  TimePattern,
  UserListSorting,
} from '@mnspro-cloud/services';
import { GroupRole, Person } from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { first, isEqual, omit } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildAzureGroupListItem } from '../../../AzureGroupSelector/buildAzureGroupListItem';
import { Button } from '../../../Button';
import { Flex } from '../../../Flex';
import { GroupPicker } from '../../../GroupPicker/GroupPicker';
import { Loader } from '../../../Loader';
import { ClassCourseForm } from './ClassCourseForm';
export interface ClassCourseProps extends ClassCourseModel {
  isLoading?: boolean;
  groups?: AzureGroup[];
  isLoadingGroups?: boolean;
  teachers?: Person[];
  isLoadingTeachers?: boolean;
  timePattern?: TimePattern;
  isLoadingTimePattern?: boolean;
  /**
   * Will be called if a user clicks on the "Add teacher" button.
   * This callback should be used to trigger loading of teachers.
   *
   * @memberof ClassCourseProps
   */
  onLinkGroupClicked?(): void;
  /**
   * Will be called if a user clicks on the "link" azure group button.
   * This callback should be used to trigger loading of groups.
   *
   * @memberof ClassCourseProps
   */
  onAddTeacherClicked?(): void;
  onSave?(classCourse: ClassCourseModel): void;
  preferenceSorting?: UserListSorting;
}

export const ClassCourse: React.FC<ClassCourseProps> = (props) => {
  const {
    id,
    courseTeachers,
    name,
    azureGroup,
    color,
    isLoading,
    occurrences,
    teachers,
    classes,
    isLoadingTeachers,
    groups,
    isLoadingGroups,
    timePattern,
    isLoadingTimePattern,
    onAddTeacherClicked,
    onLinkGroupClicked,
    onSave,
    preferenceSorting,
  } = props;

  const { t } = useTranslation();
  const originalClassCourse = useMemo(() => {
    return {
      id,
      courseTeachers,
      name,
      azureGroup,
      color,
      occurrences,
    } as ClassCourseModel;
  }, [id, courseTeachers, name, azureGroup, color, occurrences]);

  const [modifiedClassCourse, setModifiedClassCourse] = useState<ClassCourseModel>({
    id,
    courseTeachers,
    name,
    azureGroup,
    color,
    occurrences,
    classes,
  });

  const [showGroupPicker, setShowGroupPicker] = useState(false);

  const isChangeDetected = useMemo(() => {
    return (
      !isEqual(
        omit(modifiedClassCourse, 'courseTeachers', 'classes'),
        omit(originalClassCourse, 'courseTeachers', 'classes'),
      ) ||
      !isEqual(
        modifiedClassCourse?.courseTeachers.map((c) => c.id),
        originalClassCourse.courseTeachers.map((c) => c.id),
      )
    );
  }, [modifiedClassCourse, originalClassCourse]);

  const modifyClassCourse = (data: Partial<ClassCourseModel>) => {
    setModifiedClassCourse({ ...modifiedClassCourse, ...data });
  };

  return (
    <>
      <GroupPicker
        canceled={() => setShowGroupPicker(false)}
        groupsSelected={(selectedGroups) => {
          const selected = first(selectedGroups);

          if (selected === undefined) {
            return;
          }

          const original = groups?.find((g) => g.id === selected.id);

          modifyClassCourse({
            azureGroup: {
              id: selected.id,
              name: original?.name ?? selected.name,
              mailAddress: selected.mailAddress ?? '',
              role: original?.role,
              timePeriod: original?.timePeriod,
            },
          });
          setShowGroupPicker(false);
        }}
        isLoading={isLoadingGroups ?? false}
        open={showGroupPicker}
        groups={
          groups
            ?.filter((g) => g.id !== modifiedClassCourse?.azureGroup?.id)
            .map((g) => buildAzureGroupListItem(g, [GroupRole.course, GroupRole.workgroup], t)) ?? []
        }
      />
      {isLoading && <Loader />}
      {!isLoading && (
        <Flex direction="column">
          <ClassCourseForm
            activeTimePattern={timePattern}
            classCourse={modifiedClassCourse ?? originalClassCourse}
            onChange={(course) => modifyClassCourse(course)}
            groups={groups}
            teachers={teachers}
            isLoadingGroups={isLoadingGroups}
            isLoadingTeachers={isLoadingTeachers}
            onAddTeacherClicked={onAddTeacherClicked}
            onLinkGroupClicked={onLinkGroupClicked}
            preferenceSorting={preferenceSorting}
          />

          <Flex justify="flex-end" gap="0.75rem">
            <Button
              data-testid="cancelCourseButton"
              disabled={!isChangeDetected}
              onClick={() => setModifiedClassCourse(originalClassCourse)}
            >
              {t('cancel', { defaultValue: 'Abbrechen' })}
            </Button>
            <Button
              data-testid="saveCourseButton"
              disabled={!isChangeDetected || modifiedClassCourse.name.length === 0}
              appearance="primary"
              onClick={() => {
                onSave?.({
                  ...modifiedClassCourse,
                  courseTeachers: modifiedClassCourse.courseTeachers.map((t) => ({
                    ...t,
                    photo: undefined,
                  })),
                });
              }}
            >
              {t('save', { defaultValue: 'Speichern' })}
            </Button>
          </Flex>
        </Flex>
      )}
    </>
  );
};
